import React, { useState } from 'react';
import {
    IconButton,
    Box,
    Typography,
    TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../Context/ThemeContext';
import { DARKCOLORS, LIGHTCOLORS } from '../../../Utils/constants';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import SuccessNotification from '../../Common/SuccessNotification';
import { changePassword } from '../../../Services/authService';
import LoadingAnimation from '../../Common/LoadingAnimation';

function UpdatePassword({ handleClose }) {
    const { t } = useTranslation();
    const { mode } = useThemeContext();

    const btnColor = mode === 'dark' ? DARKCOLORS.Primary500 : LIGHTCOLORS.Primary500;
    const inputBoxColor = mode === 'dark' ? DARKCOLORS.GreyScale10 : LIGHTCOLORS.GreyScale10;

    const [password, setPassword] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successNotification, setSuccessNotification] = useState(false);

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;

        switch (name) {
            case 'currentPassword':
                setCurrentPassword(value);
                break;
            case 'password':
                setPassword(value);
                break;
            case 'confirmPassword':
                setConfirmPassword(value);
                break;
            default:
                break;
        }
    };

    const togglePasswordVisibility = (field) => {
        if (field === 'current') setShowCurrentPassword((prev) => !prev);
        if (field === 'new') setShowNewPassword((prev) => !prev);
        if (field === 'confirm') setShowConfirmPassword((prev) => !prev);
    };

    const validate = () => {
        const newErrors = {};
        if (!currentPassword.trim()) newErrors.currentPassword = t('navbar.currentPasswordRequired');
        if (!password.trim()) newErrors.password = t('navbar.newPasswordRequired');
        if (password.length < 5) newErrors.password = t('navbar.newPasswordLength');
        if (!confirmPassword.trim()) newErrors.confirmPassword = t('navbar.confirmPasswordRequired');
        if (password && confirmPassword && password !== confirmPassword) {
            newErrors.confirmPassword = t('navbar.passwordsDoNotMatch');
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;
        try {
            setLoading(true)
            await changePassword(currentPassword, password);
            handleClose()
            setSuccessNotification(true); // Show success notification
        } catch (error) {
            setGeneralError(error?.response?.data?.detail);
            console.error(t('navbar.errorUpdatingPassword'), error);
        } finally {
            setLoading(false)
        }
    };

    return (
        <>
            {loading && <LoadingAnimation />}
            <Typography variant="subtitle1">{t('navbar.changePassword')}</Typography>
            <TextField
                label={t('navbar.currentPassword')}
                type={showCurrentPassword ? "text" : "password"}
                name="currentPassword"
                value={currentPassword}
                onChange={handleChange}
                fullWidth
                variant={'filled'}
                error={!!errors.currentPassword}
                helperText={errors.currentPassword}
                InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                        <IconButton onClick={() => togglePasswordVisibility('current')}>
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    ),
                }}
                sx={{
                    backgroundColor: inputBoxColor,
                    borderRadius: '6px',
                    '& .MuiFilledInput-root': {
                        backgroundColor: inputBoxColor,
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: inputBoxColor },
                        '&.Mui-focused': { backgroundColor: inputBoxColor },
                    },
                }}
            />

            <TextField
                label={t('navbar.newPassword')}
                type={showNewPassword ? "text" : "password"}
                name="password"
                fullWidth
                variant={'filled'}
                value={password}
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                        <IconButton onClick={() => togglePasswordVisibility('new')}>
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    ),
                }}
                sx={{
                    backgroundColor: inputBoxColor,
                    borderRadius: '6px',
                    '& .MuiFilledInput-root': {
                        backgroundColor: inputBoxColor,
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: inputBoxColor },
                        '&.Mui-focused': { backgroundColor: inputBoxColor },
                    },
                }}
            />

            <TextField
                label={t('navbar.confirmPassword')}
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                fullWidth
                variant={'filled'}
                value={confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                InputProps={{
                    disableUnderline: true,
                    endAdornment: (
                        <IconButton onClick={() => togglePasswordVisibility('confirm')}>
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                    ),
                }}
                sx={{
                    backgroundColor: inputBoxColor,
                    borderRadius: '6px',
                    '& .MuiFilledInput-root': {
                        backgroundColor: inputBoxColor,
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: inputBoxColor },
                        '&.Mui-focused': { backgroundColor: inputBoxColor },
                    },
                }}
            />

            {generalError && (
                <Typography color="error" variant="body2" sx={{ textAlign: "center", marginTop: 1 }}>
                    {generalError}
                </Typography>
            )}
            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <button type="button" style={{ color: btnColor, borderColor: btnColor }} className='cancel-btn' onClick={handleClose}>{t('membershipCards.cancel')}</button>
                <button type="button" style={{ background: btnColor }} className='save-btn' onClick={handleSave}>{t('membershipCards.save')}</button>
            </Box>

            {/* Use Notification Component */}
            <SuccessNotification
                open={successNotification}
                message={t('membershipCards.successMessage')}
                severity="success"
                onClose={() => setSuccessNotification(false)
                }
            />
        </>
    )
}

export default UpdatePassword